import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React from 'react';
import { randomString, isDefined, _cs } from '@togglecorp/fujs';
import { IoClose, IoCalendarOutline } from '../../node_modules/react-icons/io5/index.esm.js';
import useUiModeClassName from '../../hooks/useUiModeClassName.js';
import useBlurEffect from '../../hooks/useBlurEffect.js';
import useBooleanState from '../../hooks/useBooleanState.js';
import InputContainer from '../InputContainer/index.js';
import RawInput from '../RawInput/index.js';
import RawButton from '../RawButton/index.js';
import Button from '../Button/index.js';
import Popup from '../Popup/index.js';
import Calendar from '../Calendar/index.js';
import CalendarDate from '../Calendar/CalendarDate/index.js';
import { genericMemo, ymdToDateString, dateStringToDate } from '../../utils/index.js';
import { predefinedDateRangeOptions } from './predefinedDateRange.js';
import styles from './styles.css.js';

var _IoClose, _IoCalendarOutline;

function prevMonth(date) {
  const newDate = new Date(date);
  newDate.setMonth(newDate.getMonth() - 1);
  return newDate;
}

function sameMonth(foo, bar) {
  return foo.getFullYear() === bar.getFullYear() && foo.getMonth() === bar.getMonth();
}

function DateRenderer(props) {
  const {
    className: dateClassName,
    year,
    month,
    date,
    startDate,
    endDate,
    ghost,
    ...otherProps
  } = props;
  const start = startDate ? dateStringToDate(startDate).getTime() : undefined;
  const end = endDate ? dateStringToDate(endDate).getTime() : undefined;
  const current = new Date(year, month, date).getTime();
  const inBetween = isDefined(start) && isDefined(end) && current > start && current < end;
  const dateString = ymdToDateString(year, month, date);
  const isEndDate = dateString === endDate;
  const isStartDate = dateString === startDate;
  return /*#__PURE__*/React.createElement(CalendarDate, _extends({}, otherProps, {
    className: _cs(styles.calendarDate, dateClassName, isStartDate && styles.startDate, isEndDate && styles.endDate, inBetween && styles.inBetween, ghost && styles.ghost),
    year: year,
    month: month,
    date: date,
    ghost: ghost
  }));
}

function DateRangeInput(props) {
  const {
    actions,
    actionsContainerClassName,
    className,
    disabled,
    error,
    errorContainerClassName,
    hint,
    hintContainerClassName,
    icons,
    iconsContainerClassName,
    inputSectionClassName,
    label,
    labelContainerClassName,
    readOnly,
    uiMode,
    inputElementRef,
    containerRef: containerRefFromProps,
    inputSectionRef,
    inputClassName,
    onChange,
    name,
    value,
    variant
  } = props;
  const [tempDate, setTempDate] = React.useState({
    startDate: undefined,
    endDate: undefined
  });
  const [calendarMonthSelectionPopupClassName] = React.useState(randomString(16));
  const createdContainerRef = React.useRef(null);
  const popupRef = React.useRef(null);
  const containerRef = containerRefFromProps ?? createdContainerRef;
  const uiModeClassName = useUiModeClassName(uiMode, styles.light, styles.dark);
  const [showCalendar, setShowCalendarTrue, setShowCalendarFalse,, toggleShowCalendar] = useBooleanState(false);
  const hideCalendar = React.useCallback(() => {
    setTempDate({
      startDate: undefined,
      endDate: undefined
    });
    setShowCalendarFalse();
  }, [setShowCalendarFalse]);
  const handlePopupBlur = React.useCallback((isClickedOnPopup, isClickedOnParent, e) => {
    const isClickedWithin = isClickedOnPopup || isClickedOnParent; // Following is to prevent the popup blur when
    // month selection is changed in the calendar

    const container = document.getElementsByClassName(calendarMonthSelectionPopupClassName)[0];
    const isContainerOrInsideContainer = container ? container === e.target || container.contains(e.target) : false;

    if (!isClickedWithin && !isContainerOrInsideContainer) {
      hideCalendar();
    }
  }, [hideCalendar, calendarMonthSelectionPopupClassName]);
  useBlurEffect(showCalendar, handlePopupBlur, popupRef, containerRef);
  const dateRendererParams = React.useCallback(() => ({
    startDate: tempDate.startDate ?? (value === null || value === void 0 ? void 0 : value.startDate),
    // we only set end date if user hasn't set the start date
    // i.e. to show previously selected end date)
    endDate: !tempDate.startDate ? value === null || value === void 0 ? void 0 : value.endDate : undefined
  }), [tempDate.startDate, value]);
  const handleCalendarDateClick = React.useCallback((year, month, day) => {
    setTempDate(prevTempDate => {
      if (isDefined(prevTempDate.startDate)) {
        const lastDate = ymdToDateString(year, month, day);
        const prev = dateStringToDate(prevTempDate.startDate).getTime();
        const current = new Date(year, month, day).getTime();
        const startDate = prev > current ? lastDate : prevTempDate.startDate;
        const endDate = prev > current ? prevTempDate.startDate : lastDate;
        return {
          startDate,
          endDate
        };
      }

      return {
        startDate: ymdToDateString(year, month, day),
        endDate: undefined
      };
    });
  }, []);
  React.useEffect(() => {
    if (isDefined(tempDate.endDate)) {
      if (onChange) {
        onChange(tempDate, name);
      }

      hideCalendar();
    }
  }, [tempDate, hideCalendar, onChange, name]);
  const handlePredefinedOptionClick = React.useCallback(optionKey => {
    if (onChange) {
      const option = predefinedDateRangeOptions.find(d => d.key === optionKey);

      if (option) {
        const {
          startDate,
          endDate
        } = option.getValue();
        onChange({
          startDate: ymdToDateString(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()),
          endDate: ymdToDateString(endDate.getFullYear(), endDate.getMonth(), endDate.getDate())
        }, name);
      }
    }

    hideCalendar();
  }, [onChange, hideCalendar, name]);
  const handleClearButtonClick = React.useCallback(() => {
    if (onChange) {
      onChange(undefined, name);
    }
  }, [onChange, name]);
  const endDate = value === null || value === void 0 ? void 0 : value.endDate;
  const endDateDate = endDate ? dateStringToDate(endDate) : new Date();
  const startDate = value === null || value === void 0 ? void 0 : value.startDate;
  let startDateDate = startDate ? dateStringToDate(startDate) : new Date();

  if (sameMonth(endDateDate, startDateDate)) {
    startDateDate = prevMonth(startDateDate);
  }

  const firstInitialDate = ymdToDateString(startDateDate.getFullYear(), startDateDate.getMonth(), 1);
  const secondInitialDate = ymdToDateString(endDateDate.getFullYear(), endDateDate.getMonth(), 1);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(InputContainer, {
    containerRef: containerRef,
    inputSectionRef: inputSectionRef,
    actions: /*#__PURE__*/React.createElement(React.Fragment, null, actions, !readOnly && /*#__PURE__*/React.createElement(React.Fragment, null, value && /*#__PURE__*/React.createElement(Button, {
      name: undefined,
      variant: "action",
      onClick: handleClearButtonClick,
      disabled: disabled,
      title: "Clear"
    }, _IoClose || (_IoClose = /*#__PURE__*/React.createElement(IoClose, null))), /*#__PURE__*/React.createElement(Button, {
      name: undefined,
      variant: "action",
      onClick: toggleShowCalendar,
      disabled: disabled,
      title: "Show calendar"
    }, _IoCalendarOutline || (_IoCalendarOutline = /*#__PURE__*/React.createElement(IoCalendarOutline, null))))),
    actionsContainerClassName: actionsContainerClassName,
    className: className,
    disabled: disabled,
    error: error,
    errorContainerClassName: errorContainerClassName,
    hint: hint,
    hintContainerClassName: hintContainerClassName,
    icons: icons,
    iconsContainerClassName: iconsContainerClassName,
    inputSectionClassName: inputSectionClassName,
    inputContainerClassName: styles.inputContainer,
    label: label,
    labelContainerClassName: labelContainerClassName,
    variant: variant,
    readOnly: readOnly,
    uiMode: uiMode,
    input: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(RawInput, {
      name: "startDate",
      className: _cs(styles.input, styles.startDateInput, uiModeClassName, !!error && styles.errored, !(tempDate.startDate ?? (value !== null && value !== void 0 && value.startDate)) && styles.empty, inputClassName),
      value: tempDate.startDate ?? (value === null || value === void 0 ? void 0 : value.startDate) // NOTE: Make this required to hide clear button on firefox
      ,
      required: !!(tempDate.startDate ?? (value !== null && value !== void 0 && value.startDate)),
      elementRef: inputElementRef,
      readOnly: true,
      uiMode: uiMode,
      disabled: disabled,
      onFocus: setShowCalendarTrue,
      type: "date"
    }), /*#__PURE__*/React.createElement("div", {
      className: styles.separator
    }, "to"), /*#__PURE__*/React.createElement(RawInput, {
      name: "startDate",
      className: _cs(styles.input, styles.endDateInput, uiModeClassName, !!error && styles.errored, !(value !== null && value !== void 0 && value.endDate) && styles.empty, inputClassName),
      elementRef: inputElementRef,
      readOnly: true // NOTE: Make this required to hide clear button on firefox
      ,
      required: !!(value !== null && value !== void 0 && value.endDate),
      value: value === null || value === void 0 ? void 0 : value.endDate,
      uiMode: uiMode,
      disabled: disabled,
      onFocus: setShowCalendarTrue,
      type: "date"
    }))
  }), !readOnly && showCalendar && /*#__PURE__*/React.createElement(Popup, {
    parentRef: containerRef,
    elementRef: popupRef,
    show: showCalendar,
    freeWidth: true,
    className: styles.calendarPopup,
    contentClassName: styles.popupContent
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.predefinedOptions
  }, predefinedDateRangeOptions.map(opt => /*#__PURE__*/React.createElement(RawButton, {
    className: styles.option,
    key: opt.key,
    name: opt.key,
    onClick: handlePredefinedOptionClick
  }, opt.label))), /*#__PURE__*/React.createElement(Calendar, {
    onDateClick: handleCalendarDateClick,
    className: styles.calendar,
    monthSelectionPopupClassName: calendarMonthSelectionPopupClassName,
    dateRenderer: DateRenderer,
    rendererParams: dateRendererParams,
    initialDate: firstInitialDate
  }), /*#__PURE__*/React.createElement(Calendar, {
    onDateClick: handleCalendarDateClick,
    className: styles.calendar,
    monthSelectionPopupClassName: calendarMonthSelectionPopupClassName,
    dateRenderer: DateRenderer,
    rendererParams: dateRendererParams,
    initialDate: secondInitialDate
  })));
}

var DateRangeInput$1 = genericMemo(DateRangeInput);

export { DateRangeInput$1 as default };
//# sourceMappingURL=index.js.map
